/**
 * Settlement Upload API
 * 정산서 업로드 관련 API
 */
import { apiClient } from "@/shared/api/client";
import {
    SettlementUploadHistory,
    GetUploadHistoryRequest,
    GetUploadHistoryResponse,
    UploadSettlementResponse,
    ApplyUploadResponse,
    SettlementUploadType,
} from "../types/settlementUpload.types";
import { extractArrayFromResponse } from "@/shared/utils/apiResponseHelpers";

const BASE_PATH = '/api/v1/system/settlement-upload';

/** API 응답 래퍼 타입 */
type ApiResponseWrapper<T> = T | { data?: T; items?: T; histories?: T };

/**
 * API 응답의 history 객체를 정규화
 *
 * Java boolean isApplied 필드가 JSON에서 'applied'로 직렬화될 수 있음
 */
type RawHistoryItem = SettlementUploadHistory & { applied?: boolean };

const normalizeHistory = (item: RawHistoryItem): SettlementUploadHistory => ({
    ...item,
    // 'applied' 또는 'isApplied' 필드 모두 처리
    isApplied: item.isApplied ?? item.applied ?? false,
});

const normalizeHistories = (histories: RawHistoryItem[]): SettlementUploadHistory[] =>
    histories.map(normalizeHistory);

export const settlementUploadApi = {
    /**
     * 업로드 이력 조회
     * GET /api/v1/system/settlement-upload/history
     */
    getUploadHistory: async (request?: GetUploadHistoryRequest): Promise<GetUploadHistoryResponse> => {
        const params: Record<string, string> = {};

        if (request?.startDate) {
            params.startDate = request.startDate;
        }
        if (request?.endDate) {
            params.endDate = request.endDate;
        }
        if (request?.uploadType) {
            params.uploadType = request.uploadType;
        }

        const response = await apiClient.get<ApiResponseWrapper<GetUploadHistoryResponse>>(
            `${BASE_PATH}/history`,
            { params }
        );

        const data = response.data;

        // 응답이 null 또는 undefined인 경우 빈 응답 반환
        if (!data) {
            return {
                totalCount: 0,
                histories: [],
            };
        }

        // 응답이 GetUploadHistoryResponse 형태인 경우
        if (typeof data === 'object' && 'histories' in data) {
            const rawHistories = (data as GetUploadHistoryResponse).histories || [];
            return {
                totalCount: (data as GetUploadHistoryResponse).totalCount || 0,
                histories: normalizeHistories(rawHistories as RawHistoryItem[]),
            };
        }

        // 배열로 직접 반환되는 경우
        if (Array.isArray(data)) {
            const normalized = normalizeHistories(data as RawHistoryItem[]);
            return {
                totalCount: normalized.length,
                histories: normalized,
            };
        }

        // 다른 래퍼 형태인 경우
        const rawHistories = extractArrayFromResponse<RawHistoryItem>(
            data as RawHistoryItem[] | { data?: RawHistoryItem[]; items?: RawHistoryItem[] },
            'histories'
        );
        const normalizedHistories = normalizeHistories(rawHistories);

        return {
            totalCount: normalizedHistories.length,
            histories: normalizedHistories,
        };
    },

    /**
     * 협력사 정산서 업로드
     * POST /api/v1/system/settlement-upload/partner
     */
    uploadPartnerSettlement: async (file: File, userId?: string): Promise<UploadSettlementResponse> => {
        const formData = new FormData();
        formData.append('file', file);
        if (userId) {
            formData.append('userId', userId);
        }

        const response = await apiClient.post<UploadSettlementResponse>(
            `${BASE_PATH}/partner`,
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                timeout: 300000, // 5분 타임아웃
            }
        );

        return response.data;
    },

    /**
     * 라이더 정산서 업로드
     * POST /api/v1/system/settlement-upload/rider
     */
    uploadRiderSettlement: async (file: File, userId?: string): Promise<UploadSettlementResponse> => {
        const formData = new FormData();
        formData.append('file', file);
        if (userId) {
            formData.append('userId', userId);
        }

        const response = await apiClient.post<UploadSettlementResponse>(
            `${BASE_PATH}/rider`,
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                timeout: 300000, // 5분 타임아웃
            }
        );

        return response.data;
    },

    /**
     * 정산서 업로드 (통합)
     * @param uploadType 업로드 타입 (PARTNER | RIDER)
     * @param file 엑셀 파일
     * @param userId 사용자 ID
     */
    uploadSettlement: async (
        uploadType: SettlementUploadType,
        file: File,
        userId?: string
    ): Promise<UploadSettlementResponse> => {
        if (uploadType === 'PARTNER') {
            return settlementUploadApi.uploadPartnerSettlement(file, userId);
        }
        return settlementUploadApi.uploadRiderSettlement(file, userId);
    },

    /**
     * 업로드 적용
     * POST /api/v1/system/settlement-upload/apply/{uploadId}
     */
    applyUpload: async (uploadId: string): Promise<ApplyUploadResponse> => {
        const response = await apiClient.post<ApplyUploadResponse>(
            `${BASE_PATH}/apply/${uploadId}`,
            {},
            {
                timeout: 300000, // 5분 타임아웃
            }
        );

        return response.data;
    },

    /**
     * 업로드 취소 (미적용 상태일 때만)
     * DELETE /api/v1/system/settlement-upload/{uploadId}
     */
    cancelUpload: async (uploadId: string): Promise<ApplyUploadResponse> => {
        const response = await apiClient.delete<ApplyUploadResponse>(
            `${BASE_PATH}/${uploadId}`
        );

        return response.data;
    },
};
